import * as React from 'react';

import { cn } from '../lib/cn';

export interface RadarAxis {
  label: string;
  value: number;
}

interface Point {
  x: number;
  y: number;
}

export interface RadarGeometryOptions {
  size?: number;
  max?: number;
  rings?: number;
  padding?: number;
}

export interface RadarGeometry {
  size: number;
  center: Point;
  radius: number;
  rings: string[];
  spokes: Point[];
  axes: Array<{ label: string; value: number; valuePoint: Point; labelPoint: Point }>;
  dataPolygon: string;
}

export function computeRadarGeometry(
  axes: RadarAxis[],
  options: RadarGeometryOptions = {},
): RadarGeometry {
  const size = options.size ?? 220;
  const max = options.max ?? 100;
  const ringCount = options.rings ?? 4;
  const padding = options.padding ?? 44;
  const center: Point = { x: size / 2, y: size / 2 };
  const radius = size / 2 - padding;
  const count = Math.max(axes.length, 1);
  const angleAt = (index: number): number => (-90 + (360 / count) * index) * (Math.PI / 180);
  const pointAt = (distance: number, index: number): Point => ({
    x: center.x + distance * Math.cos(angleAt(index)),
    y: center.y + distance * Math.sin(angleAt(index)),
  });
  const toPoints = (distance: number): string =>
    axes.map((_, index) => `${pointAt(distance, index).x},${pointAt(distance, index).y}`).join(' ');

  const rings: string[] = [];
  for (let ring = 1; ring <= ringCount; ring += 1) {
    rings.push(toPoints((radius * ring) / ringCount));
  }

  const computedAxes = axes.map((axis, index) => {
    const clamped = Math.max(0, Math.min(max, axis.value));
    return {
      label: axis.label,
      value: axis.value,
      valuePoint: pointAt((radius * clamped) / max, index),
      labelPoint: pointAt(radius + padding * 0.42, index),
    };
  });

  return {
    size,
    center,
    radius,
    rings,
    spokes: axes.map((_, index) => pointAt(radius, index)),
    axes: computedAxes,
    dataPolygon: computedAxes.map((axis) => `${axis.valuePoint.x},${axis.valuePoint.y}`).join(' '),
  };
}

export interface RadarSeries {
  name: string;
  values: number[];
}

const RADAR_SERIES_STYLES = [
  { line: 'stroke-primary fill-primary/15', dot: 'fill-primary', swatch: 'bg-primary' },
  { line: 'stroke-chart-4 fill-chart-4/15', dot: 'fill-chart-4', swatch: 'bg-chart-4' },
  { line: 'stroke-chart-3 fill-chart-3/15', dot: 'fill-chart-3', swatch: 'bg-chart-3' },
  { line: 'stroke-chart-5 fill-chart-5/15', dot: 'fill-chart-5', swatch: 'bg-chart-5' },
];

export function radarSeriesSwatchClass(index: number): string {
  return RADAR_SERIES_STYLES[index % RADAR_SERIES_STYLES.length]!.swatch;
}

export interface RadarCompareProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'children'> {
  labels: string[];
  series: RadarSeries[];
  ariaLabel: string;
  size?: number;
  max?: number;
  rings?: number;
}

export function RadarCompare({
  labels,
  series,
  ariaLabel,
  size = 260,
  max = 100,
  rings = 4,
  className,
  ...props
}: RadarCompareProps): React.ReactElement {
  const grid = computeRadarGeometry(
    labels.map((label) => ({ label, value: 0 })),
    { size, max, rings },
  );
  const polygons = series.map((entry) =>
    computeRadarGeometry(
      labels.map((label, index) => ({ label, value: entry.values[index] ?? 0 })),
      { size, max, rings },
    ),
  );
  return (
    <svg
      viewBox={`0 0 ${String(grid.size)} ${String(grid.size)}`}
      role="img"
      aria-label={ariaLabel}
      className={cn('text-border overflow-visible', className)}
      {...props}
    >
      <g aria-hidden="true">
        {grid.rings.map((points, index) => (
          <polygon
            key={`ring-${String(index)}`}
            points={points}
            fill="none"
            stroke="currentColor"
          />
        ))}
        {grid.spokes.map((point, index) => (
          <line
            key={`spoke-${String(index)}`}
            x1={grid.center.x}
            y1={grid.center.y}
            x2={point.x}
            y2={point.y}
            stroke="currentColor"
          />
        ))}
      </g>
      {polygons.map((geometry, seriesIndex) => {
        const style = RADAR_SERIES_STYLES[seriesIndex % RADAR_SERIES_STYLES.length]!;
        return (
          <g key={`series-${String(seriesIndex)}`}>
            <polygon
              points={geometry.dataPolygon}
              className={style.line}
              strokeWidth={2}
              strokeLinejoin="round"
            />
            {geometry.axes.map((axis, index) => (
              <circle
                key={`point-${String(seriesIndex)}-${String(index)}`}
                cx={axis.valuePoint.x}
                cy={axis.valuePoint.y}
                r={2.5}
                className={style.dot}
              />
            ))}
          </g>
        );
      })}
      {grid.axes.map((axis, index) => (
        <text
          key={`label-${String(index)}`}
          x={axis.labelPoint.x}
          y={axis.labelPoint.y}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-muted-foreground text-[9px] font-medium"
        >
          {axis.label}
        </text>
      ))}
    </svg>
  );
}

export interface RadarProps extends Omit<React.SVGAttributes<SVGSVGElement>, 'children'> {
  axes: RadarAxis[];
  ariaLabel: string;
  size?: number;
  max?: number;
  rings?: number;
}

export function Radar({
  axes,
  ariaLabel,
  size = 220,
  max = 100,
  rings = 4,
  className,
  ...props
}: RadarProps): React.ReactElement {
  const geometry = computeRadarGeometry(axes, { size, max, rings });
  return (
    <svg
      viewBox={`0 0 ${String(geometry.size)} ${String(geometry.size)}`}
      role="img"
      aria-label={ariaLabel}
      className={cn('text-border overflow-visible', className)}
      {...props}
    >
      <g aria-hidden="true">
        {geometry.rings.map((points, index) => (
          <polygon
            key={`ring-${String(index)}`}
            points={points}
            fill="none"
            stroke="currentColor"
          />
        ))}
        {geometry.spokes.map((point, index) => (
          <line
            key={`spoke-${String(index)}`}
            x1={geometry.center.x}
            y1={geometry.center.y}
            x2={point.x}
            y2={point.y}
            stroke="currentColor"
          />
        ))}
      </g>
      <polygon
        points={geometry.dataPolygon}
        className="fill-primary/20 stroke-primary"
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {geometry.axes.map((axis, index) => (
        <circle
          key={`point-${String(index)}`}
          cx={axis.valuePoint.x}
          cy={axis.valuePoint.y}
          r={2.5}
          className="fill-primary"
        />
      ))}
      {geometry.axes.map((axis, index) => (
        <text
          key={`label-${String(index)}`}
          x={axis.labelPoint.x}
          y={axis.labelPoint.y}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-muted-foreground text-[9px] font-medium"
        >
          {axis.label}
        </text>
      ))}
    </svg>
  );
}
